import { useRef } from 'react';
import { Sheet, SheetContent } from '@monksflow/monks-ui';
import { guardNotistackDismissal } from '@/shared/ui/FormDialog/formShell.utils';
import { UnsavedChangesModal } from '@/shared/ui/UnsavedChangesModal';
import { ApprovalChainEditorPanel } from '../ApprovalChainEditorPanel';
import { ApprovalChainListPanel } from '../ApprovalChainListPanel';
import type { ApprovalChainsDrawerController } from './useApprovalChainsDrawer';
import { useTranslation } from 'react-i18next';

export interface ApprovalChainsDrawerProps {
  /** From `useApprovalChainsDrawer` — owns the active subview and the dirty guard. */
  drawer: ApprovalChainsDrawerController;
  projectId: string;
}

/**
 * The project's approval chains as one right-side drawer with two subviews: the
 * list it opens on, and the editor a row switches it to. One drawer rather than
 * two stacked ones, so the editor reads as a step inside the same surface and
 * Back returns to the list without a second overlay.
 */
export function ApprovalChainsDrawer({ drawer, projectId }: ApprovalChainsDrawerProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('approvalChains');

  const { view } = drawer;

  const ariaLabel =
    view.kind === 'editor'
      ? view.target.approvalChainId
        ? t('manageApprovalChains.approvalChainEditorPanel.editApprovalChain')
        : t('manageApprovalChains.approvalChainEditorPanel.createApprovalChain')
      : t('manageApprovalChains.approvalChainListPanel.approvalChains');

  return (
    <>
      <Sheet
        open={view.kind !== 'closed'}
        onOpenChange={(isOpen) => {
          // Every close path lands here, Escape and scrim included, so the
          // dirty guard can decline and keep the sheet open.
          if (!isOpen) drawer.close();
        }}
      >
        <SheetContent
          ref={contentRef}
          // The panels head themselves, so the sheet adds no chrome of its own
          // beyond the inset every sheet in the app carries.
          showCloseButton={false}
          className="w-full gap-0 rounded-l-lg p-xl sm:max-w-160"
          data-testid="approval-chains-drawer"
          // A click on a toast is outside the sheet as far as Radix is concerned, and with a dirty
          // editor it would raise the discard prompt.
          onPointerDownOutside={guardNotistackDismissal}
          onOpenAutoFocus={(event) => {
            // Radix focuses the first focusable child, which is the close button: it
            // opens ringed, with its tooltip up, as if already pressed. Focus the panel
            // instead — the focus trap, Escape and the tab order are unchanged, and the
            // first Tab still lands on that button for anyone who wants it.
            event.preventDefault();
            contentRef.current?.focus();
          }}
          // Named rather than titled: each panel already heads itself, and a
          // SheetTitle would put the same words in the tree twice.
          aria-label={ariaLabel}
          aria-describedby={undefined}
        >
          {view.kind === 'list' && (
            <ApprovalChainListPanel
              projectId={projectId}
              onClose={drawer.close}
              onOpenEditor={drawer.openEditor}
            />
          )}

          {view.kind === 'editor' && (
            <ApprovalChainEditorPanel
              key={drawer.renderKey}
              target={view.target}
              projectId={projectId}
              onClose={drawer.close}
              onBack={drawer.openList}
              onSaved={drawer.afterSave}
              onDirtyChange={drawer.setDirty}
            />
          )}
        </SheetContent>
      </Sheet>

      <UnsavedChangesModal
        open={drawer.isPrompting}
        onCancel={drawer.cancelDiscard}
        onConfirmLeave={drawer.confirmDiscard}
      />
    </>
  );
}
